import type { CopyRenderer, CopySegment, NotificationCopy } from './types'

const ENTITY_NOUN: Record<string, string> = {
  BOOK: 'buku',
  COLLECTION: 'kompilasi buku',
  BOOK_COLLECTION: 'koleksi bacaan',
  CHALLENGE: 'tantangan',
  EXERCISE: 'latihan',
}

const RECIPIENT_VERB: Record<string, string> = {
  BOOK: 'coba baca',
  COLLECTION: 'coba baca',
  BOOK_COLLECTION: 'coba baca',
  CHALLENGE: 'coba kerjakan',
  EXERCISE: 'coba kerjakan',
}

/**
 * The Indonesian noun for a shared entity — "buku", "koleksi bacaan", "tantangan".
 *
 * Exported because the guardian mirror retells the same send to a parent and has to use the same
 * word for the thing; a second private table would drift the moment a share entity is added.
 */
export function shareEntityNoun(entityType: string | undefined): string {
  return ENTITY_NOUN[entityType ?? 'BOOK'] ?? 'konten'
}

function contextLineOf(view: Parameters<CopyRenderer>[0]): string | undefined {
  const { courseName, lessonName } = view.context
  if (lessonName && courseName) return `${lessonName} dalam tugas ${courseName}`
  return courseName ?? lessonName
}

/** "{Teacher} ingin kamu coba baca {Title}" */
export const renderShareRecipientCopy: CopyRenderer = (view) => {
  const actorName = view.snapshot.actorName ?? 'Seseorang'
  const title = view.snapshot.title ?? 'konten ini'
  const entityType = view.entity?.type ?? 'BOOK'
  const verb = RECIPIENT_VERB[entityType] ?? 'coba baca'
  // Fallback must be a real word, not '', or the trailing space in this template
  // stacks with the outer template's own space and produces a double space.
  const noun = entityType === 'BOOK' ? '' : `${ENTITY_NOUN[entityType] ?? 'konten'} `

  const copy: NotificationCopy = {
    body: [{ text: actorName, bold: true }, { text: ` ingin kamu ${verb} ${noun}` }, { text: title, bold: true }],
    contextLine: contextLineOf(view),
  }
  return copy
}

/**
 * "{Student} menerima rekomendasi buku {Title} darimu."
 * "{Student} dan {n} lainnya menerima rekomendasi buku {Title} darimu."
 */
export const renderShareActorCopy: CopyRenderer = (view) => {
  // `recipientName`, not `actorName`: on an ACTOR card the sender is "kamu", and the
  // name that opens the sentence belongs to the first recipient. Reading `actorName`
  // here made the card say "{sender} menerima rekomendasi ... darimu".
  const firstRecipient = view.snapshot.recipientName ?? 'Seorang siswa'
  const title = view.snapshot.title ?? 'konten ini'
  const entityType = view.entity?.type ?? 'BOOK'
  const noun = ENTITY_NOUN[entityType] ?? 'konten'
  const others = Math.max(0, view.recipientCount - 1)

  const body: CopySegment[] = [{ text: firstRecipient, bold: true }]
  if (others > 0) {
    body.push({ text: ' dan ' }, { text: `${others} lainnya`, bold: true })
  }
  body.push({ text: ` menerima rekomendasi ${noun} ` }, { text: title, bold: true }, { text: ' darimu.' })

  const progressLine = view.readCount > 0 ? `${view.readCount} dari ${view.recipientCount} sudah membuka` : undefined

  return { body, contextLine: contextLineOf(view), progressLine }
}
